import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MdCheckCircle } from 'react-icons/md';

const defaultState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  lookingForBabysitter: true,
  lookingForJob: false,
  lookingForNeither: false,
};

export default function RegisterQuickForm({
  onSubmit,
  isSubmitting,
  onSwitchToLogin,
  initialValues = null,
  invitedBy = '',
  invitedAsBabysitter = false,
  compact = false,
}) {
  const [form, setForm] = useState(() => ({
    ...defaultState,
    ...(initialValues || {}),
  }));
  const [error, setError] = useState('');

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const selectIntent = (intent) => {
    setForm((prev) => ({
      ...prev,
      lookingForBabysitter: intent === 'babysitter',
      lookingForJob: intent === 'job',
      lookingForNeither: intent === 'neither',
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Unesite ime i prezime.');
      return;
    }
    if (!form.email.trim()) {
      setError('Email je obavezan.');
      return;
    }
    if (form.password.trim().length < 6) {
      setError('Lozinka mora imati najmanje 6 karaktera.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError('Lozinke se ne poklapaju.');
      return;
    }
    if (!form.lookingForBabysitter && !form.lookingForJob && !form.lookingForNeither) {
      setError('Izaberite jednu od opcija ispod.');
      return;
    }

    let primaryRole = 'parent';
    if (form.lookingForJob && !form.lookingForBabysitter) {
      primaryRole = 'babysitter';
    } else if (form.lookingForNeither && invitedAsBabysitter) {
      primaryRole = 'babysitter';
    } else if (form.lookingForNeither) {
      primaryRole = 'parent';
    }

    const selectedRoles = [
      form.lookingForBabysitter ? 'parent' : null,
      form.lookingForJob ? 'babysitter' : null,
    ].filter(Boolean);

    await onSubmit({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password.trim(),
      role: primaryRole,
      preferences: {
        isLookingForBabysitter: form.lookingForBabysitter,
        isLookingForJob: form.lookingForJob,
        isLookingForNeither: form.lookingForNeither,
        invitedAsBabysitter,
        invitedBy: invitedBy || null,
        selectedRoles,
        jobSearchStatus: form.lookingForJob
          ? (form.lookingForBabysitter ? 'additional' : 'full_time')
          : null,
      },
    });
  };

  const emailLocked = Boolean(initialValues?.email);

  const inputClass = compact ? 'input-base !py-2 !text-sm' : 'input-base';
  const intentBtnClass = compact ? 'p-2.5 rounded-xl border-2 text-left transition-all' : 'p-3 rounded-2xl border-2 text-left transition-all';

  return (
    <form onSubmit={handleSubmit} className={compact ? 'space-y-3' : 'space-y-4'}>
      {invitedBy && (
        <div className={`p-2.5 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 text-gray-700 dark:text-gray-300 ${compact ? 'text-xs' : 'text-sm'}`}>
          <strong>{invitedBy}</strong> vas poziva da se pridružite Nannity platformi.
          {invitedAsBabysitter && ' Vaši podaci su unapred popunjeni — samo završite registraciju.'}
        </div>
      )}

      <div className={`grid grid-cols-1 ${compact ? 'gap-2' : 'sm:grid-cols-2 gap-3'}`}>
        <input
          type="text"
          value={form.firstName}
          onChange={(e) => updateField('firstName', e.target.value)}
          placeholder="Ime"
          className={inputClass}
          autoComplete="given-name"
          required
        />
        <input
          type="text"
          value={form.lastName}
          onChange={(e) => updateField('lastName', e.target.value)}
          placeholder="Prezime"
          className={inputClass}
          autoComplete="family-name"
          required
        />
      </div>

      <input
        type="email"
        value={form.email}
        onChange={(e) => updateField('email', e.target.value)}
        placeholder="Email adresa"
        className={`${inputClass} ${emailLocked ? 'bg-gray-50 dark:bg-gray-700/60' : ''}`}
        autoComplete="email"
        readOnly={emailLocked}
        required
      />

      <input
        type="tel"
        value={form.phone}
        onChange={(e) => updateField('phone', e.target.value)}
        placeholder="Telefon (opciono)"
        className={inputClass}
        autoComplete="tel"
      />

      <div className={`grid grid-cols-1 ${compact ? 'gap-2' : 'sm:grid-cols-2 gap-3'}`}>
        <input
          type="password"
          value={form.password}
          onChange={(e) => updateField('password', e.target.value)}
          placeholder="Lozinka"
          className={inputClass}
          autoComplete="new-password"
          minLength={6}
          required
        />
        <input
          type="password"
          value={form.confirmPassword}
          onChange={(e) => updateField('confirmPassword', e.target.value)}
          placeholder="Potvrdi lozinku"
          className={inputClass}
          autoComplete="new-password"
          minLength={6}
          required
        />
      </div>

      <div>
        <p className={`font-medium text-gray-700 dark:text-gray-300 mb-1.5 ${compact ? 'text-xs' : 'text-sm'}`}>Šta želite?</p>
        <div className={`grid grid-cols-1 ${compact ? 'gap-2' : 'sm:grid-cols-3 gap-3'}`}>
          <button
            type="button"
            onClick={() => selectIntent('babysitter')}
            className={`${intentBtnClass} ${
              form.lookingForBabysitter
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">Tražim dadilju</span>
              {form.lookingForBabysitter && <MdCheckCircle className="text-primary-600 text-xl flex-shrink-0" />}
            </div>
          </button>
          <button
            type="button"
            onClick={() => selectIntent('job')}
            className={`${intentBtnClass} ${
              form.lookingForJob
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">Tražim posao</span>
              {form.lookingForJob && <MdCheckCircle className="text-primary-600 text-xl flex-shrink-0" />}
            </div>
          </button>
          <button
            type="button"
            onClick={() => selectIntent('neither')}
            className={`${intentBtnClass} ${
              form.lookingForNeither
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-primary-300'
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">Ni jedno ni drugo</span>
              {form.lookingForNeither && <MdCheckCircle className="text-primary-600 text-xl flex-shrink-0" />}
            </div>
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {invitedAsBabysitter
            ? 'Ako ste pozvani kao dadilja, možete izabrati „Tražim posao” ili „Ni jedno ni drugo” ako vas ne zanima oglas.'
            : 'Izaberite šta vam odgovara — možete promeniti kasnije u profilu.'}
        </p>
      </div>

      {error && (
        <div className={`p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 ${compact ? 'text-xs' : 'text-sm'}`}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className={`btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed ${compact ? '!py-2.5 !text-sm' : ''}`}
      >
        {isSubmitting ? 'Kreiranje naloga...' : 'Registruj se'}
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
        Registracijom prihvatate <Link to="/terms" className="text-primary-600">Uslove korišćenja</Link> i <Link to="/privacy" className="text-primary-600">Izjavu o privatnosti</Link>.
      </p>

      {onSwitchToLogin && (
        <p className={`text-center text-gray-600 dark:text-gray-400 ${compact ? 'text-xs' : 'text-sm'}`}>
          Već imate nalog?{' '}
          <button type="button" onClick={onSwitchToLogin} className="text-primary-600 dark:text-primary-400 font-semibold">
            Uloguj se
          </button>
        </p>
      )}
    </form>
  );
}
